import { useRef } from "react";

export type DiaryHeaderProps = {
  date: string;
  onChange: (iso: string) => void;
  topOffset?: number;
  leftOffset?: number;
  rightOffset?: number;
  isFullscreen?: boolean;
};

// Telegram-капсулы («Закрыть» слева, «свернуть/меню» справа) присутствуют
// над контентом Mini App всегда (iOS / Android / desktop) — занимают
// примерно 56px по вертикали от safe-area-inset-top. Опускаем шапку ПОД
// капсулы константно, чтобы не зависеть от ненадёжного WebApp.isFullscreen
// и от того, что safeAreaInset.top на iOS при первом рендере = 0.
const TELEGRAM_CONTROLS_HEIGHT_PX = 52;

export default function DiaryHeader({
  date,
  onChange,
  topOffset = 0,
  leftOffset = 0,
  rightOffset = 0,
  isFullscreen = false,
}: DiaryHeaderProps) {
  void isFullscreen;
  const dateInputRef = useRef<HTMLInputElement>(null);

  function openCalendar() {
    const el = dateInputRef.current as (HTMLInputElement & { showPicker?: () => void }) | null;
    if (!el) return;
    if (typeof el.showPicker === "function") {
      el.showPicker();
    } else {
      el.click();
    }
  }

  const btnStyle: React.CSSProperties = {
    width: 32,
    height: 32,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.08)",
    color: "#fff",
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    padding: 0,
    WebkitTapHighlightColor: "transparent",
  };

  // Горизонтальный safe-area inset уже применён в App.tsx к корневому контейнеру.
  // Здесь — только базовый отступ 8px, иначе получаем двойной inset и контент уезжает вправо.
  void leftOffset;
  void rightOffset;
  const paddingLeft = 8;
  const paddingRight = 8;

  const extraTop = TELEGRAM_CONTROLS_HEIGHT_PX;
  // Берём максимум из всех источников safe-area + бонус под капсулы.
  // На iOS WebApp.safeAreaInset.top при первом рендере приходит как 0 —
  // тогда нас вытащит env() или CSS-переменная Telegram. Если topOffset
  // уже корректный — он победит. extraTop прибавляется ко всем кандидатам,
  // иначе при topOffset=0 шапка снова уедет под капсулы.
  const paddingTop = `max(${topOffset + extraTop}px, calc(env(safe-area-inset-top) + ${extraTop}px), calc(var(--tg-content-safe-area-inset-top, 0px) + ${extraTop}px), calc(var(--tg-safe-area-inset-top, 0px) + ${extraTop}px))`;

  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: 6,
      paddingTop,
      paddingLeft,
      paddingRight,
      height: 44,
      width: "100%",
      maxWidth: "100%",
      boxSizing: "border-box",
      minWidth: 0,
    }}>
      {/* Иконка приложения */}
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: "linear-gradient(135deg, #007AFF 0%, #5856D6 100%)",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 700,
          fontSize: 18,
          letterSpacing: -0.5,
          flexShrink: 0,
        }}
      >
        L
      </div>

      {/* Колокольчик */}
      <button
        onClick={() => alert("Уведомления — скоро")}
        aria-label="Уведомления"
        style={btnStyle}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
          <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
        </svg>
      </button>

      <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
        {/* Поиск */}
        <button
          onClick={() => alert("Поиск — скоро")}
          aria-label="Поиск"
          style={btnStyle}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <circle cx="11" cy="11" r="7" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </button>

        {/* Календарь */}
        <button
          onClick={openCalendar}
          aria-label="Выбрать дату"
          style={btnStyle}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
        </button>

        <input
          ref={dateInputRef}
          type="date"
          value={date}
          onChange={(e) => e.target.value && onChange(e.target.value)}
          style={{ position: "absolute", top: 0, left: 0, opacity: 0, pointerEvents: "none", width: 0, height: 0, overflow: "hidden" }}
          aria-hidden
          tabIndex={-1}
        />
      </div>
    </div>
  );
}
